import { useState, useEffect, useCallback } from 'react';
import {
  getFeriados, createFeriado, updateFeriado, deleteFeriado,
  getFeriadosCalendario,
} from '../services/api';

const MESES = [
  '', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const COR_TIPO = {
  fixo:        { bg: '#dcfce7', text: '#166534' },
  movel:       { bg: '#dbeafe', text: '#1e40af' },
  facultativo: { bg: '#fef9c3', text: '#854d0e' },
};

const COR_ABR = {
  nacional:  { bg: '#f0f4ff', text: '#1e3a8a' },
  estadual:  { bg: '#fef3c7', text: '#92400e' },
  municipal: { bg: '#fce7f3', text: '#9d174d' },
};

function FlagIcon({ abrangencia }) {
  if (abrangencia === 'nacional') {
    return (
      <img
        src="/flag-brazil.svg"
        alt="Brasil"
        style={{ width: 22, height: 15, borderRadius: 2, border: '0.5px solid #d1d5db', objectFit: 'cover', display: 'inline-block', verticalAlign: 'middle' }}
      />
    );
  }
  if (abrangencia === 'estadual') {
    return (
      <img
        src="/flag-pernambuco.jpg"
        alt="Pernambuco"
        style={{ width: 22, height: 15, borderRadius: 2, border: '0.5px solid #d1d5db', objectFit: 'cover', display: 'inline-block', verticalAlign: 'middle' }}
      />
    );
  }
  return null;
}

const FORM0 = {
  nome: '', tipo: 'fixo', abrangencia: 'nacional',
  estado: '', municipio: '',
  dia: '', mes: '', offset_pascoa: '',
  ativo: true, observacao: '',
};

const ABA_CADASTRO  = 'cadastro';
const ABA_CALENDARIO = 'calendario';

export default function Feriados() {
  const [aba, setAba]           = useState(ABA_CALENDARIO);
  const [feriados, setFeriados] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [filtroAbr] = useState('');
  const [filtroAtivo] = useState('true');

  // Calendário
  const anoAtual = new Date().getFullYear();
  const [ano, setAno]             = useState(anoAtual);
  const [calendario, setCalendario] = useState(null);
  const [loadingCal, setLoadingCal] = useState(false);
  const [filtroCalendario, setFiltroCalendario] = useState('');

  // Modal
  const [modal, setModal]   = useState(false);
  const [consulta, setConsulta] = useState(null);
  const [form, setForm]     = useState(FORM0);
  const [editId, setEditId] = useState(null);
  const [erro, setErro]     = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroAtivo) params.ativo = filtroAtivo;
      if (filtroAbr)   params.abrangencia = filtroAbr;
      const r = await getFeriados(params);
      setFeriados(r.data.results || r.data);
    } finally { setLoading(false); }
  }, [filtroAtivo, filtroAbr]);

  useEffect(() => { if (aba === ABA_CADASTRO) carregar(); }, [aba, carregar]);

  const buscarCalendario = useCallback(async () => {
    setLoadingCal(true);
    try {
      const r = await getFeriadosCalendario({ ano });
      setCalendario(r.data);
    } finally { setLoadingCal(false); }
  }, [ano]);

  useEffect(() => { if (aba === ABA_CALENDARIO) buscarCalendario(); }, [aba, buscarCalendario]);

  // ── form helpers ──
  const ehMovel = ['movel', 'facultativo'].includes(form.tipo);

  const abrir = (f = null) => {
    if (f) {
      setForm({
        nome:         f.nome,
        tipo:         f.tipo,
        abrangencia:  f.abrangencia,
        estado:       f.estado || '',
        municipio:    f.municipio || '',
        dia:          f.dia ?? '',
        mes:          f.mes ?? '',
        offset_pascoa: f.offset_pascoa ?? '',
        ativo:        f.ativo,
        observacao:   f.observacao || '',
      });
      setEditId(f.id);
    } else {
      setForm(FORM0);
      setEditId(null);
    }
    setErro(''); setModal(true);
  };

  const salvar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const d = {
        ...form,
        dia:           ehMovel ? null : (form.dia ? Number(form.dia) : null),
        mes:           ehMovel ? null : (form.mes ? Number(form.mes) : null),
        offset_pascoa: ehMovel && form.offset_pascoa !== '' ? Number(form.offset_pascoa) : null,
      };
      editId ? await updateFeriado(editId, d) : await createFeriado(d);
      setModal(false);
      carregar();
      buscarCalendario();
    } catch (err) {
      setErro(JSON.stringify(err.response?.data || 'Erro ao salvar'));
    } finally { setLoading(false); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este feriado?')) return;
    await deleteFeriado(id);
    carregar();
    buscarCalendario();
  };

  const anos = [];
  for (let y = anoAtual - 1; y <= anoAtual + 3; y++) anos.push(y);

  // Calendário agrupado por mês (com filtro aplicado)
  const feriadosFiltrados = calendario
    ? calendario.feriados.filter(f => {
        if (!filtroCalendario) return true;
        if (filtroCalendario === 'facultativo') return f.tipo === 'facultativo';
        return f.abrangencia === filtroCalendario;
      })
    : [];

  const porMes = feriadosFiltrados.reduce((acc, f) => {
    const m = Number(f.data.split('-')[1]);
    if (!acc[m]) acc[m] = [];
    acc[m].push(f);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 16, marginBottom: 20, borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 38, height: 38, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '1rem', marginRight: 14, flexShrink: 0 }}>
            <i className="bi bi-calendar-heart-fill" />
          </div>
          <div>
            <div style={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Gestão de Pessoal · Cadastros</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#334155', letterSpacing: '-0.5px', lineHeight: 1.2 }}>Feriados</div>
          </div>
        </div>
        <button className="btn-primary-custom" onClick={() => abrir()}>
          <i className="bi bi-plus-lg" />Novo Feriado
        </button>
      </div>

      {/* Abas + filtro de ano inline */}
      <div className="d-flex align-items-center gap-2 mb-3">
        {[
          { key: ABA_CALENDARIO, icon: 'bi-calendar3', label: 'Calendário' },
          { key: ABA_CADASTRO,   icon: 'bi-list-ul',   label: 'Cadastro'   },
        ].map(a => (
          <button
            key={a.key}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 6, fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer', border: `1px solid ${aba === a.key ? '#bfdbfe' : '#e2e8f0'}`, background: aba === a.key ? '#eff6ff' : '#f8fafc', color: aba === a.key ? '#1d4ed8' : '#64748b' }}
            onClick={() => setAba(a.key)}
          >
            <i className={`bi ${a.icon}`} />{a.label}
          </button>
        ))}

        {aba === ABA_CALENDARIO && (
          <>
            <div style={{ width: 1, height: 24, background: '#e2e8f0', margin: '0 4px' }} />
            <select
              className="form-select form-select-sm"
              style={{ minWidth: 90, width: 'auto' }}
              value={ano}
              onChange={e => { setAno(Number(e.target.value)); setFiltroCalendario(''); }}
            >
              {anos.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            {calendario && (
              <>
                <span style={{ fontSize: '0.72rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <i className="bi bi-egg text-primary" />
                  Páscoa: <strong>{calendario.pascoa}</strong>
                </span>
                <div style={{ width: 1, height: 24, background: '#e2e8f0', margin: '0 4px' }} />
                {[
                  { key: '',            label: 'Total',        count: calendario.feriados.length,                                              bg: '#f0f4ff', text: 'var(--navy)' },
                  { key: 'nacional',    label: 'Nacionais',    count: calendario.feriados.filter(f => f.abrangencia === 'nacional').length,    ...COR_ABR.nacional },
                  { key: 'estadual',    label: 'Estaduais',    count: calendario.feriados.filter(f => f.abrangencia === 'estadual').length,    ...COR_ABR.estadual },
                  { key: 'municipal',   label: 'Municipais',   count: calendario.feriados.filter(f => f.abrangencia === 'municipal').length,   ...COR_ABR.municipal },
                  { key: 'facultativo', label: 'Facultativos', count: calendario.feriados.filter(f => f.tipo === 'facultativo').length,        ...COR_TIPO.facultativo },
                ].map(s => {
                  const ativo = filtroCalendario === s.key;
                  return (
                    <div
                      key={s.label}
                      onClick={() => setFiltroCalendario(ativo && s.key !== '' ? '' : s.key)}
                      style={{
                        background: s.bg,
                        color: s.text,
                        borderRadius: 6,
                        padding: '4px 12px',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        border: ativo ? `2px solid ${s.text}` : '1px solid rgba(0,0,0,0.08)',
                        cursor: 'pointer',
                        boxShadow: ativo ? '0 0 0 2px rgba(0,0,0,0.08)' : 'none',
                        opacity: !filtroCalendario || ativo ? 1 : 0.55,
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                      }}
                      title={ativo ? 'Clique para limpar filtro' : `Filtrar por ${s.label.toLowerCase()}`}
                    >
                      <span style={{ fontWeight: 400, marginRight: 3 }}>{s.label}:</span>{s.count}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>

      {/* ── ABA CALENDÁRIO ── */}
      {aba === ABA_CALENDARIO && (
        <>
          {loadingCal && (
            <div className="d-flex justify-content-center py-5">
              <span className="spinner-border text-primary me-2" />
              <span className="text-muted">Carregando calendário...</span>
            </div>
          )}

          {!loadingCal && calendario && (
            <>
              {/* Grid 12 meses */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => {
                  const lista = porMes[m] || [];
                  if (filtroCalendario && lista.length === 0) return null;
                  // altura mínima = header(30) + 3 linhas(40px cada)
                  const minH = 30 + 3 * 40;
                  return (
                    <div key={m} style={{ border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column', minHeight: minH }}>
                      {/* Cabeçalho do mês */}
                      <div style={{ background: lista.length ? 'var(--navy)' : '#94a3b8', color: '#fff', padding: '6px 12px', fontWeight: 700, fontSize: '0.76rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span><i className="bi bi-calendar3 me-2" style={{ opacity: 0.7 }} />{MESES[m]}</span>
                        {lista.length > 0 && (
                          <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 10, padding: '0px 7px', fontSize: '0.65rem', fontWeight: 600 }}>
                            {lista.length}
                          </span>
                        )}
                      </div>

                      {/* Feriados do mês */}
                      {lista.length === 0 ? (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: '0.7rem' }}>
                          <i className="bi bi-calendar-x" style={{ fontSize: '1.1rem', marginBottom: 4 }} />
                          Sem feriados
                        </div>
                      ) : (
                        lista.map((f, idx) => {
                          const borderColor = f.abrangencia === 'nacional' ? '#3b82f6' : f.abrangencia === 'estadual' ? '#f59e0b' : '#ec4899';
                          const diaSemana = ['dom','seg','ter','qua','qui','sex','sáb'][new Date(f.data + 'T00:00:00').getDay()];
                          return (
                            <div key={f.id} style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '5px 12px',
                              borderBottom: idx < lista.length - 1 ? '1px solid #f1f5f9' : 'none',
                              borderLeft: `3px solid ${borderColor}`,
                              background: idx % 2 === 0 ? '#fff' : '#fafbff',
                            }}>
                              {/* Data */}
                              <div style={{ minWidth: 34, textAlign: 'center' }}>
                                <div style={{ fontWeight: 800, fontSize: '0.92rem', color: 'var(--navy)', lineHeight: 1 }}>
                                  {f.data_br.slice(0, 2)}
                                </div>
                                <div style={{ fontSize: '0.57rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>
                                  {diaSemana}
                                </div>
                              </div>
                              {/* Separador */}
                              <div style={{ width: 1, height: 24, background: '#e2e8f0' }} />
                              {/* Nome */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '0.74rem', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {f.nome}
                                </div>
                              </div>
                              {/* Badges + bandeira — lado direito */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                <span style={{ ...COR_ABR[f.abrangencia], borderRadius: 3, padding: '0px 5px', fontSize: '0.57rem', fontWeight: 700 }}>
                                  {f.abrangencia === 'municipal' && f.municipio ? f.municipio : f.abrangencia_display}
                                </span>
                                <span style={{ ...COR_TIPO[f.tipo], borderRadius: 3, padding: '0px 5px', fontSize: '0.57rem', fontWeight: 700 }}>
                                  {f.tipo_display}
                                </span>
                                <FlagIcon abrangencia={f.abrangencia} />
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      {/* ── ABA CADASTRO ── */}
      {aba === ABA_CADASTRO && (
        <>
          <div className="card-ponto">
            <div className="table-responsive">
              <table className="table-ponto" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ paddingLeft: 32 }}>Nome</th>
                    <th>Data / Regra</th>
                    <th>Tipo</th>
                    <th>Abrangência</th>
                    <th>Status</th>
                    <th style={{ width: 80 }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={6} className="text-center py-3">
                      <span className="spinner-border spinner-border-sm text-primary" />
                    </td></tr>
                  )}
                  {!loading && feriados.map(f => (
                    <tr key={f.id} style={{ cursor: 'pointer' }} onClick={() => setConsulta(f)}>
                      <td style={{ fontWeight: 600, paddingLeft: 32 }}>{f.nome}</td>
                      <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        {f.tipo === 'fixo'
                          ? `${String(f.dia).padStart(2,'0')}/${String(f.mes).padStart(2,'0')}`
                          : f.offset_pascoa != null
                            ? `Páscoa ${f.offset_pascoa >= 0 ? '+' : ''}${f.offset_pascoa}d`
                            : '—'
                        }
                      </td>
                      <td>
                        <span style={{ ...COR_TIPO[f.tipo], borderRadius: 3, padding: '1px 8px', fontSize: '0.68rem', fontWeight: 700 }}>
                          {f.tipo_display}
                        </span>
                      </td>
                      <td>
                        <span style={{ ...COR_ABR[f.abrangencia], borderRadius: 3, padding: '1px 8px', fontSize: '0.68rem', fontWeight: 700 }}>
                          {f.abrangencia_display}
                          {f.estado ? ` · ${f.estado}` : ''}
                          {f.municipio ? ` · ${f.municipio}` : ''}
                        </span>
                      </td>
                      <td>
                        <span style={{ background: f.ativo ? '#dcfce7' : '#fee2e2', color: f.ativo ? '#166534' : '#991b1b', borderRadius: 3, padding: '1px 8px', fontSize: '0.68rem', fontWeight: 700 }}>
                          {f.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <button style={{ background: 'none', border: '1px solid #bfdbfe', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#1d4ed8', fontSize: '0.75rem', marginRight: 6 }} title="Editar" onClick={() => abrir(f)}>
                          <i className="bi bi-pencil" />
                        </button>
                        <button style={{ background: 'none', border: '1px solid #fecaca', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#dc2626', fontSize: '0.75rem' }} title="Excluir" onClick={() => excluir(f.id)}>
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!loading && feriados.length === 0 && (
                    <tr><td colSpan={6}>
                      <div className="empty-state"><i className="bi bi-calendar-x" /><p>Nenhum feriado encontrado</p></div>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── Modal Cadastro / Edição ── */}
      {modal && (
        <div className="modal-backdrop-custom" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal-box" style={{ maxWidth: 560 }}>
            <div className="modal-header-custom">
              <h5><i className="bi bi-calendar-plus me-2" />{editId ? 'Editar Feriado' : 'Novo Feriado'}</h5>
              <button className="modal-close" onClick={() => setModal(false)}><i className="bi bi-x-lg" /></button>
            </div>
            <form onSubmit={salvar}>
              <div className="modal-body-custom">
                {erro && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '8px 12px', color: '#991b1b', fontSize: 'var(--fs-xs)', marginBottom: 12 }}>
                    {erro}
                  </div>
                )}

                <div className="row g-2">
                  <div className="col-12">
                    <label className="form-label">Nome *</label>
                    <input className="form-control" required value={form.nome}
                      onChange={e => setForm({ ...form, nome: e.target.value })}
                      placeholder="Ex: Tiradentes, Carnaval..." />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Tipo *</label>
                    <select className="form-select" value={form.tipo}
                      onChange={e => setForm({ ...form, tipo: e.target.value, dia: '', mes: '', offset_pascoa: '' })}>
                      <option value="fixo">Fixo</option>
                      <option value="movel">Móvel</option>
                      <option value="facultativo">Facultativo</option>
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Abrangência *</label>
                    <select className="form-select" value={form.abrangencia}
                      onChange={e => setForm({ ...form, abrangencia: e.target.value, estado: '', municipio: '' })}>
                      <option value="nacional">Nacional</option>
                      <option value="estadual">Estadual</option>
                      <option value="municipal">Municipal</option>
                    </select>
                  </div>

                  {form.abrangencia !== 'nacional' && (
                    <div className="col-md-4">
                      <label className="form-label">UF *</label>
                      <input className="form-control" maxLength={2} required value={form.estado}
                        onChange={e => setForm({ ...form, estado: e.target.value.toUpperCase() })}
                        placeholder="PE" />
                    </div>
                  )}

                  {form.abrangencia === 'municipal' && (
                    <div className="col-12">
                      <label className="form-label">Município *</label>
                      <input className="form-control" required value={form.municipio}
                        onChange={e => setForm({ ...form, municipio: e.target.value })}
                        placeholder="Ex: Recife" />
                    </div>
                  )}

                  {/* Fixo: dia e mês */}
                  {!ehMovel && (
                    <>
                      <div className="col-md-3">
                        <label className="form-label">Dia *</label>
                        <input type="number" className="form-control" required min={1} max={31}
                          value={form.dia} onChange={e => setForm({ ...form, dia: e.target.value })}
                          placeholder="01" />
                      </div>
                      <div className="col-md-5">
                        <label className="form-label">Mês *</label>
                        <select className="form-select" required value={form.mes}
                          onChange={e => setForm({ ...form, mes: e.target.value })}>
                          <option value="">Selecione</option>
                          {MESES.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                        </select>
                      </div>
                    </>
                  )}

                  {/* Móvel: offset da Páscoa */}
                  {ehMovel && (
                    <div className="col-md-6">
                      <label className="form-label">Offset da Páscoa (dias) *</label>
                      <input type="number" className="form-control" required
                        value={form.offset_pascoa}
                        onChange={e => setForm({ ...form, offset_pascoa: e.target.value })}
                        placeholder="Ex: -47 (Carnaval), -2 (6ª Santa), 60 (Corpus)" />
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 3 }}>
                        Negativo = antes da Páscoa · Positivo = depois
                      </div>
                    </div>
                  )}

                  <div className="col-12">
                    <label className="form-label">Observação</label>
                    <input className="form-control" value={form.observacao}
                      onChange={e => setForm({ ...form, observacao: e.target.value })}
                      placeholder="Observações opcionais..." />
                  </div>

                  <div className="col-12">
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="feriado-ativo"
                        checked={form.ativo} onChange={e => setForm({ ...form, ativo: e.target.checked })} />
                      <label className="form-check-label" htmlFor="feriado-ativo" style={{ fontWeight: 600 }}>Ativo</label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer-custom">
                <button type="button" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', color: '#475569', fontSize: '0.72rem', fontWeight: 600 }} onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary-custom" disabled={loading}>
                  {loading ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-check-lg" />Salvar</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal Consulta ── */}
      {consulta && (
        <div className="modal-backdrop-custom" onClick={e => e.target === e.currentTarget && setConsulta(null)}>
          <div className="modal-box" style={{ maxWidth: 460 }}>
            <div className="modal-header-custom">
              <h5><i className="bi bi-calendar-event me-2" />Detalhe do Feriado</h5>
              <button className="modal-close" onClick={() => setConsulta(null)}><i className="bi bi-x-lg" /></button>
            </div>
            <div className="modal-body-custom">
              <div className="row g-2">
                {[
                  ['Nome',        consulta.nome],
                  ['Tipo',        <span style={{ ...COR_TIPO[consulta.tipo], borderRadius: 3, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 700 }}>{consulta.tipo_display}</span>],
                  ['Abrangência', <span style={{ ...COR_ABR[consulta.abrangencia], borderRadius: 3, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 700 }}>{consulta.abrangencia_display}{consulta.estado ? ` · ${consulta.estado}` : ''}{consulta.municipio ? ` · ${consulta.municipio}` : ''}</span>],
                  ['Data / Regra', consulta.tipo === 'fixo'
                    ? `${String(consulta.dia).padStart(2,'0')}/${String(consulta.mes).padStart(2,'0')} (todo ano)`
                    : consulta.offset_pascoa != null
                      ? `Páscoa ${consulta.offset_pascoa >= 0 ? '+' : ''}${consulta.offset_pascoa} dias`
                      : '—'],
                  ['Status', <span style={{ background: consulta.ativo ? '#dcfce7' : '#fee2e2', color: consulta.ativo ? '#166534' : '#991b1b', borderRadius: 3, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 700 }}>{consulta.ativo ? 'Ativo' : 'Inativo'}</span>],
                  consulta.observacao ? ['Observação', consulta.observacao] : null,
                ].filter(Boolean).map(([label, value]) => (
                  <div key={label} className="col-12" style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ minWidth: 100, color: 'var(--text-muted)', fontSize: '0.72rem' }}>{label}</span>
                    <span style={{ fontWeight: 600, fontSize: '0.78rem' }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer-custom">
              <button type="button" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', color: '#475569', fontSize: '0.72rem', fontWeight: 600 }} onClick={() => setConsulta(null)}>Fechar</button>
              <button type="button" className="btn-primary-custom" onClick={() => { setConsulta(null); abrir(consulta); }}>
                <i className="bi bi-pencil me-1" />Editar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
